import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import { sanitize } from '@ibe/services';
import { ApiPackageDetails, ApiPackageModel } from '@ibe/api';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/package-details.en.t.json';
import Keys from '../../../_Translations/generated/package-details.en.t.json.keys';
import useSliding from '../../Hooks/useSliding';
import ItineraryContent from './ItineraryContext';
import ImportantInfo from './ImportantInfo';

interface Props {
  item: ApiPackageModel;
  packageDetails: ApiPackageDetails;
  isSmall: boolean;
}

const PackageDetailsTabs = observer(function PackageDetailsTabs(props: Props) {
  const { item, packageDetails, isSmall } = props;
  const { itineraries } = packageDetails;

  const { t } = useTranslation('package-details', fallback, Keys);

  const getInitialActiveTab = (): string => {
    if (item.description) {
      return '1';
    }
    if (item.inclusions) {
      return '2';
    }
    if (itineraries && itineraries.length > 0) {
      return '3';
    }
    if (item.importantInfo && item.importantInfo.length > 0) {
      return '4';
    }
    return '0';
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab);

  const {
    scrollState,
    noScroll,
    scrollRight,
    scrollLeft,
    innerRef,
    outerRef,
    scrollRef
  } = useSliding();

  const showTabs =
    item.description ||
    item.inclusions ||
    (itineraries && itineraries.length > 0) ||
    (item.importantInfo && item.importantInfo.length > 0);

  if (!showTabs) {
    return <></>;
  }

  const toggle = (tab: string): void => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <div className="iso__package-details__tabs">
      <div
        ref={outerRef}
        className={`iso__package-details__tabs__scroll__outer${
          noScroll ? ' ibe-tabs__outer--no-scroll' : ''
        }`}
      >
        <button
          type="button"
          aria-label="scroll left"
          className={`ibe-tabs__left d-lg-none${scrollState.isLeft ? ' ibe-tabs__fadeout' : ''}`}
          onClick={scrollLeft}
        />
        <div className="iso__package-details__tabs__scroll" ref={scrollRef}>
          <div className="d-inline-block" ref={innerRef}>
            <Nav tabs style={{ cursor: 'pointer' }}>
              {item.description && (
                <NavItem
                  className={activeTab === '1' ? 'iso__package-details__tabs--active active' : ''}
                >
                  <NavLink
                    className={activeTab === '1' ? 'active' : ''}
                    onClick={(): void => {
                      toggle('1');
                    }}
                  >
                    {t(Keys.about)}
                  </NavLink>
                </NavItem>
              )}
              {item.inclusions && (
                <NavItem>
                  <NavLink
                    className={activeTab === '2' ? 'active' : ''}
                    onClick={(): void => {
                      toggle('2');
                    }}
                  >
                    {t(Keys.inclusions)}
                  </NavLink>
                </NavItem>
              )}
              {itineraries && itineraries.length > 0 && (
                <NavItem>
                  <NavLink
                    className={activeTab === '3' ? 'active' : ''}
                    onClick={(): void => {
                      toggle('3');
                    }}
                  >
                    {t(Keys.itinerary)}
                  </NavLink>
                </NavItem>
              )}
              {item.importantInfo && item.importantInfo.length > 0 && (
                <NavItem>
                  <NavLink
                    className={activeTab === '4' ? 'active' : ''}
                    onClick={(): void => {
                      toggle('4');
                    }}
                  >
                    {t(Keys.important_info)}
                  </NavLink>
                </NavItem>
              )}
            </Nav>
          </div>
        </div>
        <button
          type="button"
          aria-label="scroll right"
          className={`ibe-tabs__right d-lg-none${scrollState.isRight ? ' ibe-tabs__fadeout' : ''}`}
          onClick={scrollRight}
        />
      </div>
      <TabContent activeTab={activeTab} className="px-2">
        <TabPane tabId="1">
          <Row>
            <Col sm="12" className="pt-2">
              <div dangerouslySetInnerHTML={{ __html: sanitize(item.description) }} />
            </Col>
          </Row>
        </TabPane>
        <TabPane tabId="2">
          <Row>
            <Col sm="12" className="pt-2">
              {item.inclusions && (
                <div dangerouslySetInnerHTML={{ __html: sanitize(item.inclusions) }} />
              )}
            </Col>
          </Row>
        </TabPane>
        <TabPane tabId="3">
          <Row className="ml-2">
            <Col sm="12" className="pt-2">
              {itineraries && itineraries?.length > 0 && (
                <ItineraryContent itineraries={itineraries} isSmall={isSmall} />
              )}
            </Col>
          </Row>
        </TabPane>
        <TabPane tabId="4">
          <Row>
            <Col sm="12" className="pt-2">
              <ImportantInfo importantInfo={item.importantInfo} />
            </Col>
          </Row>
        </TabPane>
      </TabContent>
    </div>
  );
});

export default PackageDetailsTabs;
